import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React from 'react';

interface SavedEntryLine {
  id: number;
  description: string;
  debit: number;
  credit: number;
  documentKey: string;
  node: {
    accountCode: string;
    key: string;
  };
}

interface SavedEntry {
  id: number;
  name: string;
  status: string;
  date: string;
  lines: SavedEntryLine[];
}

interface SavedEntryTooltipContentProps {
  savedEntry: SavedEntry;
}

const SavedEntryTooltipContent: React.FC<SavedEntryTooltipContentProps> = ({
  savedEntry,
}) => {
  const totalDebit = savedEntry.lines.reduce((sum, l) => sum + l.debit, 0);
  const totalCredit = savedEntry.lines.reduce((sum, l) => sum + l.credit, 0);

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 600,
        maxWidth: 850,
        maxHeight: 450,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          pb: 1,
          borderBottom: `2px solid #0288d1`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: '#0288d1' }}
          >
            Asiento #{savedEntry.id}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: BASE_COLORS.gray, fontWeight: 500 }}
        >
          {formatDate(new Date(savedEntry.date), 'dd-mm-yyyy')}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, maxHeight: 320, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                  whiteSpace: 'nowrap',
                }}
              >
                Cuenta
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Nombre
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Descripción
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                  whiteSpace: 'nowrap',
                }}
              >
                Documento
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                  whiteSpace: 'nowrap',
                }}
              >
                Debe
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                  whiteSpace: 'nowrap',
                }}
              >
                Haber
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {savedEntry.lines.map((line, idx) => (
              <TableRow
                key={idx}
                sx={{
                  '&:hover': { bgcolor: `${BASE_COLORS.softGray}30` },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {line.node?.accountCode}
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 150,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.8rem',
                  }}
                  title={line.node?.key}
                >
                  {line.node?.key}
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.8rem',
                  }}
                  title={line.description}
                >
                  {line.description}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {line.documentKey}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                >
                  {formatMoney(line.debit)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                >
                  {formatMoney(line.credit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 3,
          mt: 1.5,
          pt: 1.5,
          borderTop: `2px solid ${BASE_COLORS.softGray}`,
          bgcolor: `${BASE_COLORS.softGray}20`,
          mx: -2,
          mb: -2,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Totales:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', minWidth: 90, textAlign: 'right' }}
        >
          {formatMoney(totalDebit)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', minWidth: 90, textAlign: 'right' }}
        >
          {formatMoney(totalCredit)}
        </Typography>
      </Box>
    </Box>
  );
};

export default SavedEntryTooltipContent;
export type { SavedEntry, SavedEntryLine };
